import {
	GovAccordion,
	GovAccordionItem,
	GovTag,
} from '@gov-design-system-ce/react';
import { CmsPageRender, MarkdownRender } from '@gov-nx/component/web';
import type { ExtraConfig } from '@gov-nx/core/app';
import { useRezaDetailContext, useRezaLocale } from '@gov-nx/module/page';
import {
	Button,
	ButtonGroupLayout,
	ContentLayout,
	CopyButton,
	DataBoxSelect,
	DataTable,
	ErrorStatusBanner,
	InfoTooltip,
	LayoutGap,
} from '@gov-nx/ui/web';
import { ComplaintsModal } from '../ComplaintsModal';
import { InvalidationModal } from '../InvalidationModal';
import { RezaDetailSkeleton } from './RezaDetailSkeleton';
import { GovFormWeb } from '@gov-design-system-ce/react-form';

type RezaDetailPageProps =
	ExtraConfig['pageRoutes']['rezaDetailConfig']['rezaDetail'];

export const RezaDetailPage = (props: RezaDetailPageProps) => {
	const { t } = useRezaLocale();
	const {
		code,
		isLoading,
		isError,
		data,
		name,
		description,
		onDownload,
		isDownloading,
		complaintsForm,
		sendByDataBoxForm,
		isEditModalOpen,
		keyValues,
		postoupeni,
		portalInfo,
	} = useRezaDetailContext();

	if (isLoading) {
		return <RezaDetailSkeleton />;
	}

	if (isError) {
		return <ErrorStatusBanner code={code} />;
	}

	return (
		<CmsPageRender
			code={code}
			tag={
				props.isHeadlineTagVisible
					? data?.role === 'zmocnitel'
						? t('detail.hlavicka.zmocnitel')
						: t('detail.hlavicka.zmocnenec')
					: undefined
			}
			override={{
				name,
				description,
			}}
			showFavorite={props.isFavoriteToggleVisible}>
			<ContentLayout>
				<LayoutGap
					gap="xl"
					className="mt-0!">
					{props.isHeadlineVisible ? (
						<>
							{data?.typ === 'implicitni' ? (
								<h3 className="text-headline-s!">
									{t('detail.implicitni.title')}
								</h3>
							) : null}
							{data?.typ === 'explicitni' ? (
								<h3 className="text-headline-s!">
									{t('detail.explicitni.title')}
								</h3>
							) : null}
						</>
					) : null}

					<DataTable responsive={true}>
						<tbody>
							{keyValues.map(({ key, tooltip, value, canCopy }) => (
								<tr key={key}>
									<td className="md:w-1/2">
										{key}
										{tooltip?.children ? (
											<InfoTooltip
												content={
													<MarkdownRender>
														{tooltip.children.toString()}
													</MarkdownRender>
												}
											/>
										) : null}
									</td>

									<td className="md:w-1/2">
										<strong>{value ?? '-'}</strong>
										{canCopy && value ? (
											<CopyButton
												label={t('detail.buttons.zkopirovat-do-schranky')}
												value={value}
											/>
										) : null}
									</td>
								</tr>
							))}
						</tbody>
					</DataTable>

					{props.isRozsahVisible && data?.rozsah?.length ? (
						<div className="pl-(--templates-margin-s)">
							<p className="text-body-s!">{t('detail.rozsah')}</p>
							<div className="flex flex-wrap gap-(--spacing-s-nudge) mt-(--templates-margin-s)!">
								{data.rozsah.map((item) => (
									<GovTag
										key={item}
										color="primary"
										type="subtle"
										size="xs">
										{item}
									</GovTag>
								))}
							</div>
						</div>
					) : null}

					{props.isSeznamVisible && postoupeni.length ? (
						<GovAccordion
							size="l"
							noBorder>
							<GovAccordionItem
								open
								label={<h3>{t('detail.seznam-postoupeni.title')}</h3>}>
								<ul className="timeline-list">
									{postoupeni.map((p, i) => (
										<li key={p.title}>
											{i === 0 && (
												<GovTag
													color="neutral"
													type="subtle"
													size="xs"
													className="inline-block mb-(--spacing-s)!">
													{t('detail.tag.zmocnitel')}
												</GovTag>
											)}
											<h3 className="text-headline-s! text-(--text-primary-color)">
												{p.title}
											</h3>
											<dl className="mt-(--spacing-s)! text-(--text-secondary)">
												{p.keyValues.map((item) => (
													<div key={item.key}>
														<dt className="inline">
															{item.key}
															{': '}
														</dt>
														<dd className="inline">
															<strong>{item.value}</strong>
														</dd>
													</div>
												))}
											</dl>
											<span className="timeline-line" />
											<span className="timeline-arrow" />
										</li>
									))}
								</ul>
							</GovAccordionItem>
						</GovAccordion>
					) : null}

					{props.isSendByDataBoxVisible ? (
						<GovFormWeb
							id={sendByDataBoxForm.formId}
							formDefinition={sendByDataBoxForm.formDefinition}
							onSubmit={sendByDataBoxForm.onSubmit}>
							{sendByDataBoxForm.hasDataBoxes ? (
								<DataBoxSelect
									{...sendByDataBoxForm.formDefinition.fields.datovaSchrankaId}
								/>
							) : null}
						</GovFormWeb>
					) : null}

					{props.isActionsVisible ? (
						<ButtonGroupLayout>
							{props.isComplaintVisible ? (
								data?.stav === 'platne' && data?.typ === 'implicitni' ? (
									<Button
										onClick={complaintsForm.isModalOpen.setTrue}
										color="primary"
										isDisabled={isDownloading}
										type="outlined"
										size="m"
										isExpandedMobile>
										{t('detail.buttons.reklamace')}
									</Button>
								) : null
							) : null}

							{props.isInvalidationVisible &&
							portalInfo &&
							data?.stav === 'platne' &&
							data?.typ === 'explicitni' ? (
								<Button
									onClick={isEditModalOpen.setTrue}
									color="primary"
									type="outlined"
									size="m"
									isExpandedMobile>
									{t('detail.buttons.ukoncit')}
								</Button>
							) : null}

							{sendByDataBoxForm.hasDataBoxes &&
							props.isSendByDataBoxVisible ? (
								<Button
									form={sendByDataBoxForm.formId}
									isLoading={sendByDataBoxForm.isLoading}
									isDisabled={sendByDataBoxForm.isLoading}
									nativeType="submit"
									color="primary"
									type="outlined">
									{sendByDataBoxForm.strings.sendByDataboxButton}
								</Button>
							) : null}

							{props.isDownloadButtonVisible ? (
								<Button
									onClick={onDownload}
									isLoading={isDownloading}
									isDisabled={isDownloading}
									color="primary"
									type="solid"
									size="m"
									isExpandedMobile>
									{t('detail.buttons.pdf')}
								</Button>
							) : null}
						</ButtonGroupLayout>
					) : null}
				</LayoutGap>

				{props.isComplaintVisible ? (
					<ComplaintsModal complaintsForm={complaintsForm} />
				) : null}

				{props.isInvalidationVisible && portalInfo ? (
					<InvalidationModal
						isEditModalOpen={isEditModalOpen}
						portalInfo={portalInfo}
					/>
				) : null}
			</ContentLayout>
		</CmsPageRender>
	);
};
